/* ss-video-visibility.js — autoplaying videos run only while they are on screen,
   and they RUN.
   (2026-09-13.) The resin and oil pages carry three to six looping, muted,
   autoplay videos (the "how to use" steps, the scent notes, the rarity scene).
   Nothing paused them: once a section had scrolled into view its videos decoded
   every frame for the rest of the visit, off screen, on every page. Measured
   on /products/hojari-blend at 1440 x 2 in headed Chrome: three videos still
   playing at the foot of a 21,686px page; with them stopped the worst scroll
   frame fell from 50ms to 18ms, and on a laptop under load that continuous
   decode is exactly what reads as "the page hangs".
   (2026-09-14, Shruti on macOS Safari 27.) Every scene on the jade oil page sat
   frozen on its poster. The page had asked once, swallowed the refusal, and
   never asked again. Safari refuses a first play() for reasons the page cannot
   see — Low Power Mode, a per-site "Never Auto-Play", a tab opened in the
   background — and the theme's own reduced-motion branch withheld the scenes
   entirely. Sho's call: these are the product's own hand-drawn scenes and they
   should simply play. So reduced motion no longer holds them back, and a refusal
   is not final: the video is asked again when it can play, on the first touch,
   click or key anywhere on the page (Safari lifts its block on a gesture), and
   whenever the tab comes back to the front.
   One observer for every [autoplay] video on the page, including ones added
   later (cart drawer, sections rehydrated in the editor). A video is played
   when it enters the viewport with a margin and paused when it leaves. Videos
   the page itself paused (data-ss-keep) and videos with controls are left
   alone. */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window)) return;
  var seen = new WeakSet();
  var onScreen = new Set();       /* intersecting right now (a Set: the retries walk it) */
  var wanting = [];               /* refused while on screen; asked again on the next chance */

  function play(v) {
    if (!v.paused) return;
    v.muted = true;                /* the attribute alone is not enough for every Safari */
    var p;
    try { p = v.play(); } catch (e) { return; }
    if (p && p.catch) p.catch(function () { if (wanting.indexOf(v) < 0) wanting.push(v); });
  }
  function retry() {
    var list = wanting; wanting = [];
    for (var i = 0; i < list.length; i++) if (onScreen.has(list[i])) play(list[i]);
  }
  /* the tab coming back: the browser pauses media in a hidden tab and on a bfcache
     restore without any promise rejecting, so every on-screen video is asked, not
     only the refused ones (play() is a no-op on one that is running) */
  function resume() { onScreen.forEach(play); }
  /* a gesture anywhere lifts a Safari block */
  ['touchend', 'pointerdown', 'keydown'].forEach(function (n) {
    document.addEventListener(n, retry, { passive: true, capture: true });
  });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) resume(); });
  window.addEventListener('pageshow', resume);

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var v = entries[i].target;
      if (entries[i].isIntersecting) {
        onScreen.add(v);
        play(v);
      } else {
        onScreen.delete(v);
        if (!v.paused) v.pause();
      }
    }
  }, { rootMargin: '25% 0px 25% 0px', threshold: 0 });
  function watch(v) {
    if (seen.has(v) || v.controls || v.hasAttribute('data-ss-keep')) return;
    seen.add(v);
    /* a source that arrives later (the deferred data-src pattern) or a play() that
       lost a race with load(): ask again the moment there is something to play */
    v.addEventListener('canplay', function () { if (onScreen.has(v)) play(v); });
    io.observe(v);
  }
  function scan(root) {
    var list = (root || document).querySelectorAll('video[autoplay]');
    for (var i = 0; i < list.length; i++) watch(list[i]);
  }
  scan();
  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.tagName === 'VIDEO') watch(n); else if (n.querySelector) scan(n);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
